/**
 * Standard retry for transient failures (network, 5xx, timeout, rate limit).
 * 3 attempts, exponential backoff (500ms, 1s, 2s).
 */

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 500;

export class RetryableError extends Error {
  constructor(message?: string) {
    super(message ?? 'Retryable');
    this.name = 'RetryableError';
  }
}

/** Consider error retryable: 408/429/5xx or message hints (timeout, network). */
export function isRetryableError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const o = err as Record<string, unknown>;
  const status = o.status ?? o.statusCode;
  if (typeof status === 'number') {
    if (status === 408 || status === 429) return true;
    if (status >= 500 && status < 600) return true;
  }
  const msg = String(o.message ?? '').toLowerCase();
  if (/timeout|econnreset|econnrefused|network|fetch failed|failed to fetch/.test(msg)) return true;
  return false;
}

export interface RetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
}

/**
 * Runs fn(); on rejection or RetryableError, retries with exponential backoff.
 * After maxAttempts, rethrows or returns last result.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      const retryable = e instanceof RetryableError || isRetryableError(e);
      if (!retryable || attempt === maxAttempts) throw e;
      const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  throw lastError;
}

/** Result shape from Supabase / PostgREST (data + error). */
export type MutationResult<T> = { data: T | null; error: unknown };

/**
 * Runs a mutation (e.g. Supabase .update().select()) with retry on transient errors.
 * Single place for "if error retryable → throw RetryableError; else return result".
 */
export async function withRetryMutation<T>(
  run: () => Promise<MutationResult<T>>,
  options: RetryOptions = {}
): Promise<MutationResult<T>> {
  return withRetry(async () => {
    const result = await run();
    if (result.error != null && isRetryableError(result.error)) {
      const msg =
        typeof (result.error as { message?: string }).message === 'string'
          ? (result.error as { message: string }).message
          : 'Retryable';
      throw new RetryableError(msg);
    }
    return result;
  }, options);
}
